import { useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Calendar as CalendarIcon, Loader2, LogOut, ExternalLink, AlertCircle, Copy, Mail, BookOpen, ChevronDown, ChevronRight, UserPlus, X, RefreshCw, Video } from "lucide-react";
import { formatDateLong, formatTime12h, formatMoney } from "@/lib/api";

const Admin = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState(() => localStorage.getItem("bravely_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [status, setStatus] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [outreach, setOutreach] = useState({ clients: [], behind_count: 0, total: 0, gap_days: 21 });
  const [showAllOutreach, setShowAllOutreach] = useState(false);
  const [openReflections, setOpenReflections] = useState({}); // { root_id: true }
  const [loading, setLoading] = useState(false);
  const [showAddPartnership, setShowAddPartnership] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "",
    email: "",
    phone: "",
    package_id: "package_12",
    session_date: "",
    session_time: "14:00",
    notes: "",
  });
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(null); // root_id currently syncing
  const [cgNext, setCgNext] = useState(null);
  const [cgSchedule, setCgSchedule] = useState([]);
  const [autoScheduling, setAutoScheduling] = useState(false);

  const loadCg = async () => {
    try {
      const r = await api.get("/connecting-grounds/next");
      if (r.data?.scheduled) setCgNext(r.data);
    } catch {}
  };

  const autoSchedule = async () => {
    if (!window.confirm("Auto-create the next 12 months of 2nd-Friday Connecting Grounds gatherings? Existing dates will be skipped.")) return;
    setAutoScheduling(true);
    try {
      const r = await api.post(`/admin/connecting-grounds/auto-schedule`, {
        months: 12, session_time: "10:00", timezone: "America/New_York", notify_signups: false,
      }, { params: { token } });
      toast.success(`${r.data.created_count} new gathering${r.data.created_count === 1 ? "" : "s"} created · ${r.data.skipped_exists} already existed`);
      setCgSchedule(r.data.schedule || []);
      loadCg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-schedule failed");
    } finally {
      setAutoScheduling(false);
    }
  };

  const retrySync = async (rootId) => {
    setSyncing(rootId);
    try {
      const r = await api.post(`/admin/bookings/${rootId}/sync-calendar`, null, { params: { token } });
      if (r.data.hangout_link) {
        toast.success("Meet link ready — booking synced to your calendar.");
      } else {
        toast.success("Calendar synced.");
      }
      loadAll(token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — try reconnecting Google Calendar first.");
    } finally {
      setSyncing(null);
    }
  };

  const createPartnership = async () => {
    if (!newClient.name || !newClient.email || !newClient.session_date || !newClient.session_time) {
      toast.error("Name, email, date and time are all required.");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post(`/admin/partnerships`, newClient, { params: { token } });
      toast.success(`${r.data.booking.name} added — rebook link ready to share.`);
      setShowAddPartnership(false);
      setNewClient({ name: "", email: "", phone: "", package_id: "package_12", session_date: "", session_time: "14:00", notes: "" });
      loadAll(token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create partnership");
    } finally {
      setCreating(false);
    }
  };

  const headers = { token };

  const loadAll = async (t) => {
    setLoading(true);
    try {
      const [sRes, bRes, oRes] = await Promise.all([
        api.get(`/oauth/calendar/status`, { params: { token: t } }),
        api.get(`/admin/bookings`, { params: { token: t } }),
        api.get(`/admin/outreach`, { params: { token: t } }),
      ]);
      setStatus(sRes.data);
      setBookings(bRes.data.bookings || []);
      setOutreach(oRes.data || { clients: [], behind_count: 0, total: 0, gap_days: 21 });
      setAuthed(true);
      localStorage.setItem("bravely_admin_token", t);
      loadCg();
    } catch (e) {
      setAuthed(false);
      if (e?.response?.status === 401) toast.error("Wrong admin token");
      else toast.error("Could not load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadAll(token);
    if (searchParams.get("calendar") === "connected") {
      toast.success(`Calendar connected: ${searchParams.get("email") || ""}`);
      searchParams.delete("calendar");
      searchParams.delete("email");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const connect = async () => {
    try {
      const res = await api.get(`/oauth/calendar/login`, { params: { token } });
      window.location.href = res.data.authorization_url;
    } catch {
      toast.error("Could not start Google OAuth.");
    }
  };

  const disconnect = async () => {
    try {
      await api.post(`/oauth/calendar/disconnect`, null, { params: { token } });
      toast.success("Calendar disconnected.");
      loadAll(token);
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  if (!authed) {
    return (
      <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-24" data-testid="admin-login">
        <div className="max-w-md mx-auto px-6">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-4">Admin</p>
          <h1 className="font-serif text-4xl text-[#4F5D4F] leading-tight mb-8">
            Sign in, Jess.
          </h1>
          <p className="text-[#4F5D4F]/80 mb-6 font-light">
            Enter your admin token to access bookings and calendar settings.
          </p>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="rounded-none border-[#D7B2A3] bg-white h-12"
            data-testid="admin-token-input"
          />
          <button
            onClick={() => loadAll(token)}
            disabled={!token || loading}
            className="mt-5 w-full bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] disabled:opacity-50 py-4 text-xs tracking-eyebrow uppercase"
            data-testid="admin-login-button"
          >
            {loading ? "Checking…" : "Continue"}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-24" data-testid="admin-page">
      <div className="max-w-[1300px] mx-auto px-6 sm:px-12 lg:px-16">
        <div className="flex items-baseline justify-between mb-12">
          <div>
            <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">Admin dashboard</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#4F5D4F] leading-tight">
              Hello, Jess.
            </h1>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem("bravely_admin_token");
              setAuthed(false);
              setToken("");
            }}
            className="text-xs tracking-eyebrow uppercase text-[#4F5D4F] hover:text-[#B89B63] flex items-center gap-2"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {/* Calendar connection */}
        <section className="bg-white p-8 border border-[#D7B2A3]/50 mb-10" data-testid="admin-calendar-card">
          <div className="flex items-center gap-4 mb-4">
            <CalendarIcon className="text-[#B89B63]" size={22} />
            <h2 className="font-serif text-2xl text-[#4F5D4F]">Google Calendar</h2>
          </div>
          {status?.connected ? (
            status?.healthy === false ? (
              <div data-testid="admin-calendar-unhealthy">
                <div className="bg-[#B89B63]/15 border-l-4 border-[#B89B63] p-4 mb-5">
                  <p className="flex items-center gap-2 text-[#4F5D4F] font-medium mb-1">
                    <AlertCircle size={18} className="text-[#B89B63]" />
                    Token expired — bookings are NOT syncing to your calendar.
                  </p>
                  <p className="text-sm text-[#4F5D4F]/80 mt-1">
                    Connected as <strong>{status.email}</strong>, but Google refused the stored token. New bookings won't appear on your calendar and clients won't receive Meet links until you reconnect.
                  </p>
                  {status.error && (
                    <p className="text-[10px] text-[#8C9A88] font-mono mt-2 break-all">{status.error}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={connect}
                    className="inline-flex items-center gap-3 bg-[#B89B63] text-[#F6F1EA] hover:bg-[#a08750] px-6 py-3 text-xs tracking-eyebrow uppercase"
                    data-testid="admin-calendar-reconnect"
                  >
                    Reconnect Google Calendar <ExternalLink size={14} />
                  </button>
                  <button
                    onClick={disconnect}
                    className="text-xs tracking-eyebrow uppercase text-[#4F5D4F] hover:text-[#B89B63]"
                    data-testid="admin-calendar-disconnect-broken"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="flex items-center gap-2 text-[#4F5D4F]" data-testid="admin-calendar-connected">
                  <CheckCircle2 size={18} className="text-[#B89B63]" />
                  Connected as <strong className="font-medium">{status.email}</strong>
                  {status?.healthy === true && (
                    <span className="text-[10px] tracking-eyebrow uppercase bg-[#4F5D4F]/10 text-[#4F5D4F] px-2 py-1 ml-2" data-testid="admin-calendar-healthy">Live · verified</span>
                  )}
                </p>
                <p className="text-sm text-[#8C9A88] mt-2">
                  Every paid booking is now added to your primary Google Calendar and the client is invited by email.
                </p>
                <button
                  onClick={disconnect}
                  className="mt-6 border border-[#4F5D4F] text-[#4F5D4F] hover:bg-[#4F5D4F] hover:text-[#F6F1EA] px-6 py-3 text-xs tracking-eyebrow uppercase"
                  data-testid="admin-calendar-disconnect"
                >
                  Disconnect
                </button>
              </div>
            )
          ) : (
            <div>
              <p className="text-[#4F5D4F]/80 font-light">
                Connect your Google Calendar once. From then on, every confirmed booking will appear on your calendar
                with the client invited.
              </p>
              <button
                onClick={connect}
                className="mt-6 inline-flex items-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-8 py-4 text-xs tracking-eyebrow uppercase"
                data-testid="admin-calendar-connect"
              >
                Connect Google Calendar <ExternalLink size={14} />
              </button>
            </div>
          )}
        </section>

        {/* Connecting Grounds — auto-schedule */}
        <section className="bg-white p-8 border border-[#D7B2A3]/50 mb-10" data-testid="admin-cg-card">
          <div className="flex items-center gap-4 mb-4">
            <CalendarIcon className="text-[#B89B63]" size={22} />
            <h2 className="font-serif text-2xl text-[#4F5D4F]">Connecting Grounds</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Next gathering</p>
              {cgNext ? (
                <p className="font-serif text-xl text-[#4F5D4F]" data-testid="admin-cg-next">
                  {new Date(`${cgNext.session_date}T${cgNext.session_time}:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                  <span className="text-[#8C9A88] font-light text-base"> · {cgNext.session_time} ET</span>
                </p>
              ) : (
                <p className="text-[#8C9A88] italic" data-testid="admin-cg-none">No gathering scheduled yet.</p>
              )}
              <p className="text-sm text-[#8C9A88] mt-2">Free monthly gathering — 2nd Friday of each month, 10:00 AM ET.</p>
            </div>
            <div className="md:text-right md:self-center">
              <button
                onClick={autoSchedule}
                disabled={autoScheduling}
                className="inline-flex items-center gap-2 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] disabled:opacity-40 px-6 py-3 text-xs tracking-eyebrow uppercase"
                data-testid="admin-cg-auto-schedule"
              >
                {autoScheduling ? <><Loader2 className="animate-spin" size={14} /> Scheduling…</> : "Auto-schedule next 12 months"}
              </button>
            </div>
          </div>
        </section>

        {/* Outreach — Partnership clients needing follow-up */}
        <section className="bg-white border border-[#D7B2A3]/50 mb-10" data-testid="admin-outreach-card">
          <div className="p-8 border-b border-[#D7B2A3]/30 flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <AlertCircle className="text-[#B89B63]" size={20} />
                <h2 className="font-serif text-2xl text-[#4F5D4F]">Clients needing outreach</h2>
              </div>
              <p className="text-sm text-[#8C9A88] mt-2" data-testid="admin-outreach-count">
                {outreach.behind_count > 0
                  ? `${outreach.behind_count} Partnership ${outreach.behind_count === 1 ? "client is" : "clients are"} behind (no upcoming session + ${outreach.gap_days}+ days quiet).`
                  : outreach.total > 0
                    ? "Everyone's on pace. Nothing to chase today."
                    : "No Partnership clients yet."}
              </p>
            </div>
            {outreach.total > 0 && (
              <button
                onClick={() => setShowAllOutreach((v) => !v)}
                className="text-xs tracking-eyebrow uppercase text-[#4F5D4F] hover:text-[#B89B63]"
                data-testid="admin-outreach-toggle-all"
              >
                {showAllOutreach
                  ? (outreach.behind_count > 0 ? "Show behind only" : "Hide list")
                  : `Show all ${outreach.total} Partnership ${outreach.total === 1 ? "client" : "clients"}`}
              </button>
            )}
            <button
              onClick={() => setShowAddPartnership(true)}
              className="inline-flex items-center gap-2 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-4 py-2 text-xs tracking-eyebrow uppercase"
              data-testid="admin-add-partnership-open"
            >
              <UserPlus size={14} /> Add Partnership Client
            </button>
          </div>

          {outreach.clients.length === 0 ? (
            <p className="p-12 text-[#8C9A88]" data-testid="admin-outreach-empty">No Partnership clients tracked yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F1EA] text-[#8C9A88] text-xs tracking-eyebrow uppercase">
                  <tr>
                    <th className="text-left p-4">Client</th>
                    <th className="text-left p-4">Progress</th>
                    <th className="text-left p-4">Last session</th>
                    <th className="text-left p-4">Next session</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllOutreach ? outreach.clients : outreach.clients.filter((c) => c.is_behind)).map((c) => (
                    <Fragment key={c.root_id}>
                    <tr
                      className={`border-t border-[#D7B2A3]/30 ${c.is_behind ? "bg-[#D7B2A3]/10" : ""}`}
                      data-testid={`admin-outreach-row-${c.root_id}`}
                    >
                      <td className="p-4">
                        <div className="text-[#4F5D4F] font-medium">{c.name}</div>
                        <div className="text-[#8C9A88] text-xs">{c.email}</div>
                        <div className="text-[#8C9A88] text-[10px] tracking-eyebrow uppercase mt-1">
                          {c.package_id === "membership_monthly" ? "Monthly Partnership" : "Upfront Partnership"}
                        </div>
                      </td>
                      <td className="p-4 text-[#4F5D4F]">
                        <div className="text-base font-medium">{c.sessions_used} <span className="text-[#8C9A88] font-normal">of 12</span></div>
                        <div className="text-xs text-[#8C9A88]">{c.sessions_remaining} remaining</div>
                        {c.reflection_count > 0 && (
                          <button
                            onClick={() => setOpenReflections((p) => ({ ...p, [c.root_id]: !p[c.root_id] }))}
                            className="mt-2 inline-flex items-center gap-1 text-[10px] tracking-eyebrow uppercase text-[#B89B63] hover:text-[#4F5D4F]"
                            data-testid={`admin-outreach-reflections-toggle-${c.root_id}`}
                          >
                            {openReflections[c.root_id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            <BookOpen size={12} /> {c.reflection_count} {c.reflection_count === 1 ? "reflection" : "reflections"}
                          </button>
                        )}
                      </td>
                      <td className="p-4 text-[#4F5D4F]">
                        {c.last_session_date ? (
                          <>
                            <div>{formatDateLong(c.last_session_date)}</div>
                            <div className="text-xs text-[#8C9A88]">{c.days_since_last_activity} days ago</div>
                          </>
                        ) : (
                          <span className="text-[#8C9A88] text-xs">No past sessions yet</span>
                        )}
                      </td>
                      <td className="p-4 text-[#4F5D4F]">
                        {c.next_session_date ? (
                          <div>{formatDateLong(c.next_session_date)}</div>
                        ) : (
                          <span className="text-[#B89B63] text-xs italic">Not booked</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-3 py-1 text-[10px] tracking-eyebrow uppercase ${
                            c.is_behind
                              ? "bg-[#B89B63] text-[#F6F1EA]"
                              : "bg-[#4F5D4F]/10 text-[#4F5D4F]"
                          }`}
                          data-testid={`admin-outreach-status-${c.root_id}`}
                        >
                          {c.is_behind ? "Behind" : "On pace"}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(c.rebook_url);
                              toast.success("Rebook link copied");
                            }}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-eyebrow uppercase text-[#4F5D4F] border border-[#4F5D4F] px-3 py-2 hover:bg-[#4F5D4F] hover:text-[#F6F1EA]"
                            data-testid={`admin-outreach-copy-${c.root_id}`}
                          >
                            <Copy size={12} /> Copy link
                          </button>
                          <a
                            href={`mailto:${c.email}?subject=${encodeURIComponent("Ready for your next session?")}&body=${encodeURIComponent(`Hi ${c.name || "there"},\n\nJust checking in — you have ${c.sessions_remaining} sessions left in your Partnership. Whenever you're ready, you can book your next one here:\n\n${c.rebook_url}\n\nWith love,\nJess`)}`}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-eyebrow uppercase text-[#F6F1EA] bg-[#4F5D4F] px-3 py-2 hover:bg-[#3a453a]"
                            data-testid={`admin-outreach-email-${c.root_id}`}
                          >
                            <Mail size={12} /> Email
                          </a>
                          {!c.hangout_link && (
                            <button
                              onClick={() => retrySync(c.root_id)}
                              disabled={syncing === c.root_id}
                              className="inline-flex items-center gap-1.5 text-[10px] tracking-eyebrow uppercase text-[#B89B63] border border-[#B89B63] px-3 py-2 hover:bg-[#B89B63] hover:text-[#F6F1EA] disabled:opacity-40"
                              title="No Google Meet link yet — retry calendar sync"
                              data-testid={`admin-outreach-sync-${c.root_id}`}
                            >
                              {syncing === c.root_id ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
                              Retry Meet
                            </button>
                          )}
                          {c.hangout_link && (
                            <a
                              href={c.hangout_link}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-[10px] tracking-eyebrow uppercase text-[#8C9A88] hover:text-[#4F5D4F]"
                              data-testid={`admin-outreach-meet-${c.root_id}`}
                            >
                              <Video size={12} /> Meet
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                    {openReflections[c.root_id] && c.reflections && c.reflections.length > 0 && (
                      <tr className="border-t border-[#D7B2A3]/20 bg-[#F6F1EA]/60" data-testid={`admin-outreach-reflections-panel-${c.root_id}`}>
                        <td colSpan={6} className="p-6">
                          <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88] mb-4 flex items-center gap-2">
                            <BookOpen size={12} /> {c.name?.split(" ")[0]}'s reflections — most recent first
                          </p>
                          <div className="space-y-4">
                            {c.reflections.map((r, i) => (
                              <div key={i} className="bg-white border-l-2 border-[#B89B63] p-4">
                                <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-2">
                                  Session · {formatDateLong(r.session_date)}
                                </p>
                                <p className="text-[#4F5D4F] font-light leading-relaxed whitespace-pre-wrap font-serif italic">
                                  "{r.text}"
                                </p>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                  {!showAllOutreach && outreach.behind_count === 0 && outreach.total > 0 && (
                    <tr>
                      <td colSpan={6} className="p-12 text-center text-[#8C9A88]" data-testid="admin-outreach-none-behind">
                        Everyone's on pace. Toggle "Show all" to see every Partnership client.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Add Partnership modal */}
        {showAddPartnership && (
          <div
            className="fixed inset-0 bg-[#4F5D4F]/60 z-50 flex items-center justify-center p-4"
            onClick={() => !creating && setShowAddPartnership(false)}
            data-testid="admin-add-partnership-modal"
          >
            <div
              className="bg-[#F6F1EA] border border-[#D7B2A3] max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-[#D7B2A3]/50">
                <div>
                  <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-1">New Partnership</p>
                  <h3 className="font-serif text-2xl text-[#4F5D4F]">Add a client who paid outside Stripe</h3>
                </div>
                <button
                  onClick={() => !creating && setShowAddPartnership(false)}
                  className="text-[#8C9A88] hover:text-[#4F5D4F]"
                  data-testid="admin-add-partnership-close"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Full name</label>
                  <Input
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    placeholder="Sadie Cure"
                    className="rounded-none border-[#D7B2A3] bg-white h-11"
                    data-testid="admin-add-name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Email</label>
                  <Input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    placeholder="sadie@example.com"
                    className="rounded-none border-[#D7B2A3] bg-white h-11"
                    data-testid="admin-add-email"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Phone (optional)</label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    placeholder="+1 555 123 4567"
                    className="rounded-none border-[#D7B2A3] bg-white h-11"
                    data-testid="admin-add-phone"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Partnership type</label>
                  <select
                    value={newClient.package_id}
                    onChange={(e) => setNewClient({ ...newClient, package_id: e.target.value })}
                    className="w-full rounded-none border border-[#D7B2A3] bg-white h-11 px-3 text-[#4F5D4F]"
                    data-testid="admin-add-package"
                  >
                    <option value="package_12">Upfront Partnership ($999 — one payment)</option>
                    <option value="membership_monthly">Monthly Partnership ($185/mo)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">First session date</label>
                    <Input
                      type="date"
                      value={newClient.session_date}
                      onChange={(e) => setNewClient({ ...newClient, session_date: e.target.value })}
                      className="rounded-none border-[#D7B2A3] bg-white h-11"
                      data-testid="admin-add-date"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Time (ET)</label>
                    <Input
                      type="time"
                      value={newClient.session_time}
                      onChange={(e) => setNewClient({ ...newClient, session_time: e.target.value })}
                      className="rounded-none border-[#D7B2A3] bg-white h-11"
                      data-testid="admin-add-time"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Private notes (optional)</label>
                  <textarea
                    value={newClient.notes}
                    onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                    placeholder="Anything to remember about this client…"
                    className="w-full rounded-none border border-[#D7B2A3] bg-white p-3 min-h-[80px] text-[#4F5D4F] font-light"
                    data-testid="admin-add-notes"
                  />
                </div>
                <p className="text-xs text-[#8C9A88] italic">
                  Confirmation email + Google Calendar event with Meet link will be created automatically. They'll receive their rebook + reflect links in the email.
                </p>
              </div>
              <div className="p-6 border-t border-[#D7B2A3]/50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowAddPartnership(false)}
                  disabled={creating}
                  className="text-xs tracking-eyebrow uppercase text-[#4F5D4F] px-5 py-3 hover:text-[#B89B63] disabled:opacity-40"
                  data-testid="admin-add-partnership-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={createPartnership}
                  disabled={creating}
                  className="inline-flex items-center gap-2 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] disabled:opacity-40 px-6 py-3 text-xs tracking-eyebrow uppercase"
                  data-testid="admin-add-partnership-submit"
                >
                  {creating ? <><Loader2 className="animate-spin" size={14} /> Creating…</> : "Create Partnership"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bookings */}
        <section className="bg-white border border-[#D7B2A3]/50" data-testid="admin-bookings-card">
          <div className="p-8 border-b border-[#D7B2A3]/30">
            <h2 className="font-serif text-2xl text-[#4F5D4F]">All bookings</h2>
            <p className="text-sm text-[#8C9A88] mt-1">{bookings.length} total</p>
          </div>
          {loading ? (
            <div className="p-12 flex items-center gap-3 text-[#4F5D4F]">
              <Loader2 className="animate-spin" size={18} /> Loading…
            </div>
          ) : bookings.length === 0 ? (
            <p className="p-12 text-[#8C9A88]">No bookings yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F1EA] text-[#8C9A88] text-xs tracking-eyebrow uppercase">
                  <tr>
                    <th className="text-left p-4">Client</th>
                    <th className="text-left p-4">Session</th>
                    <th className="text-left p-4">Offering</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Calendar</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-[#D7B2A3]/30" data-testid={`admin-booking-${b.id}`}>
                      <td className="p-4">
                        <div className="text-[#4F5D4F] font-medium">{b.name}</div>
                        <div className="text-[#8C9A88] text-xs">{b.email}</div>
                      </td>
                      <td className="p-4 text-[#4F5D4F]">
                        <div>{formatDateLong(b.session_date)}</div>
                        <div className="text-xs text-[#8C9A88]">{formatTime12h(b.session_time)} ET</div>
                      </td>
                      <td className="p-4 text-[#4F5D4F] text-xs">
                        {b.package_id === "individual" ? `Single · ${formatMoney(175)}` : `12-Session · ${formatMoney(1800)}`}
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-3 py-1 text-[10px] tracking-eyebrow uppercase ${
                            b.status === "confirmed"
                              ? "bg-[#4F5D4F] text-[#F6F1EA]"
                              : b.status === "cancelled"
                              ? "bg-[#D7B2A3]/40 text-[#4F5D4F]"
                              : "bg-[#B89B63]/20 text-[#B89B63]"
                          }`}
                        >
                          {b.status}
                        </span>
                      </td>
                      <td className="p-4 text-xs text-[#8C9A88]">
                        {b.calendar_event_id ? (
                          <span className="text-[#4F5D4F]">✓ Synced</span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default Admin;
